// Custom inline SVG icon set for GPA Scales.
//
// These icons share a single visual language with the sidebar nav icons
// (`src/components/Layout.tsx`): 16-unit viewbox where relevant, 1.5–1.75
// stroke weight, round line caps/joins, `currentColor` for stroke/fill. This
// deliberately replaces the lucide-react defaults (`GraduationCap`, `Check`,
// `X`, `Star`, `AlertTriangle`), which read as the generic "AI icon pack" and
// mixed two different stroke weights on the same screen. Keeping the set
// hand-rolled (no dependency) also lets the brand mark stay distinctive.

import { type SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & {
  'aria-hidden'?: boolean | 'true' | 'false';
};

const baseSvgProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * Brand mark — a stylized mortarboard with tassel. Replaces lucide's
 * `GraduationCap`. Drawn as a flat diamond top + band + hanging tassel so it
 * reads as academic without copying the literal lucide silhouette.
 */
export function CapIcon({ className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden={rest['aria-hidden'] ?? true}
      {...baseSvgProps}
      {...rest}
    >
      {/* Diamond mortarboard top */}
      <path d="M12 3.5 3 8l9 4.5L21 8l-9-4.5z" />
      {/* Cap band — the curved underside that gives it dimension */}
      <path d="M6 10.2v4.3c0 .9 2.7 2.5 6 2.5s6-1.6 6-2.5v-4.3" />
      {/* Tassel cord draped from the right corner */}
      <path d="M21 8v5.2" />
      <circle cx="21" cy="14.8" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Confident single-stroke check. Replaces lucide's `Check`. Slightly heavier
 * stroke than the lucide default so it reads at h-3 alongside body copy.
 */
export function CheckIcon({ className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden={rest['aria-hidden'] ?? true}
      {...baseSvgProps}
      {...rest}
    >
      <path d="M4.5 12.8 9.2 17.6 19.5 6.9" />
    </svg>
  );
}

/**
 * Clean diagonal close. Replaces lucide's `X`. Used both as a "no/missing"
 * indicator in the contribution table and as a close affordance on overlays.
 */
export function XIcon({ className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden={rest['aria-hidden'] ?? true}
      {...baseSvgProps}
      {...rest}
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

/**
 * Five-point star drawn from a single path. Replaces lucide's `Star`. Supports
 * a `fill` prop ("currentColor" / "none") so the shortlist toggle can show
 * filled vs. outline states. The points are slightly blunted (round joins) so
 * it reads as an academic seal rather than a generic UI star.
 */
export function StarIcon({
  className,
  fill = 'none',
  ...rest
}: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill={fill}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={rest['aria-hidden'] ?? true}
      {...rest}
    >
      <path d="M12 3.4l2.55 5.55 6.05.6-4.55 4.05 1.35 5.95L12 16.9l-5.4 2.65 1.35-5.95L3.4 9.55l6.05-.6L12 3.4z" />
    </svg>
  );
}

/**
 * Warning triangle with an exclamation mark. Replaces lucide's `AlertTriangle`.
 * Used by the ErrorBoundary for both inline and full-page fallbacks.
 */
export function AlertIcon({ className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden={rest['aria-hidden'] ?? true}
      {...baseSvgProps}
      {...rest}
    >
      <path d="M12 3.6 21.4 19.8H2.6L12 3.6z" />
      <path d="M12 9.8v4.2" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}
